import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import api from "../../services/AxiosInstance";

// =====================================================
// FETCH RECEIPT REPORT
// GET /api/reports/receipt-report
// =====================================================

export const fetchReceiptReportThunk =
  createAsyncThunk(
    "receiptReport/fetch",
    async (
      filters = {},
      { rejectWithValue },
    ) => {
      try {
        const params = {
          from_date:
            filters?.from_date || "",

          to_date:
            filters?.to_date || "",

          ward_id:
            filters?.ward_id || "",

          agent_id:
            filters?.agent_id || "",

          scope:
            filters?.scope || "all",
        };

        const res = await api.get(
          "/api/reports/receipt-report",
          {
            params,
          },
        );

        if (!res.data?.success) {
          return rejectWithValue(
            res.data?.message ||
              "Failed to load report",
          );
        }

        return res.data?.data || [];
      } catch (err) {
        return rejectWithValue(
          err?.response?.data?.message ||
            err?.message ||
            "Server error",
        );
      }
    },
  );

// =====================================================
// RECEIPT REPORT EXPORT
// GET /api/reports/receipt-report/export
// format = excel | pdf
// =====================================================

export const exportReceiptReportThunk =
  createAsyncThunk(
    "receiptReport/export",
    async (
      {
        format,
        from_date = "",
        to_date = "",
        ward_id = "",
        agent_id = "",
        scope = "all",
        search = "",
        sort_field = "rct_no",
        sort_direction = "desc",
      },
      { rejectWithValue },
    ) => {
      try {
        if (
          !["excel", "pdf"].includes(
            format,
          )
        ) {
          return rejectWithValue(
            "Invalid export format",
          );
        }

        const response = await api.get(
          "/api/reports/receipt-report/export",
          {
            params: {
              format,
              from_date,
              to_date,
              ward_id,
              agent_id,
              scope,
              search,
              sort_field,
              sort_direction,
            },

            responseType: "blob",
          },
        );

        const contentType =
          format === "pdf"
            ? "application/pdf"
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

        const blob = new Blob(
          [response.data],
          {
            type: contentType,
          },
        );

        let fileName =
          scope === "water"
            ? "water_receipt_report"
            : scope === "slrm"
              ? "slrm_receipt_report"
              : "all_receipt_report";

        fileName +=
          format === "pdf"
            ? ".pdf"
            : ".xlsx";

        const disposition =
          response.headers?.[
            "content-disposition"
          ];

        if (disposition) {
          const match =
            disposition.match(
              /filename="?([^"]+)"?/i,
            );

          if (match?.[1]) {
            fileName =
              match[1].trim();
          }
        }

        const url =
          window.URL.createObjectURL(
            blob,
          );

        const link =
          document.createElement("a");

        link.href = url;

        link.setAttribute(
          "download",
          fileName,
        );

        document.body.appendChild(
          link,
        );

        link.click();
        link.remove();

        window.URL.revokeObjectURL(
          url,
        );

        return {
          success: true,
          format,
          fileName,
        };
      } catch (err) {
        console.error(
          "Receipt report export error:",
          err,
        );

        try {
          if (
            err?.response?.data instanceof
            Blob
          ) {
            const text =
              await err.response.data.text();

            const json =
              JSON.parse(text);

            return rejectWithValue(
              json?.message ||
                "Failed to export report",
            );
          }
        } catch (_) {
          // Ignore blob parsing error
        }

        return rejectWithValue(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to export report",
        );
      }
    },
  );

// =====================================================
// SLICE
// =====================================================

const receiptReportSlice =
  createSlice({
    name: "receiptReport",

    initialState: {
      rows: [],
      loading: false,
      error: null,

      exportLoading: false,
      exportError: null,
      exportFormat: null,
    },

    reducers: {
      clearReceiptReport: (
        state,
      ) => {
        state.rows = [];
        state.loading = false;
        state.error = null;

        state.exportLoading = false;
        state.exportError = null;
        state.exportFormat = null;
      },

      clearReceiptExportError: (
        state,
      ) => {
        state.exportError = null;
      },
    },

    extraReducers: (
      builder,
    ) => {
      builder
        // FETCH REPORT
        .addCase(
          fetchReceiptReportThunk.pending,
          (state) => {
            state.loading = true;
            state.error = null;
          },
        )

        .addCase(
          fetchReceiptReportThunk.fulfilled,
          (
            state,
            action,
          ) => {
            state.loading = false;
            state.rows =
              action.payload || [];
          },
        )

        .addCase(
          fetchReceiptReportThunk.rejected,
          (
            state,
            action,
          ) => {
            state.loading = false;
            state.error =
              action.payload ||
              "Failed to load report";
          },
        )

        // EXPORT REPORT
        .addCase(
          exportReceiptReportThunk.pending,
          (
            state,
            action,
          ) => {
            state.exportLoading = true;
            state.exportError = null;
            state.exportFormat =
              action.meta.arg?.format ||
              null;
          },
        )

        .addCase(
          exportReceiptReportThunk.fulfilled,
          (state) => {
            state.exportLoading = false;
            state.exportError = null;
            state.exportFormat = null;
          },
        )

        .addCase(
          exportReceiptReportThunk.rejected,
          (
            state,
            action,
          ) => {
            state.exportLoading = false;
            state.exportError =
              action.payload ||
              "Failed to export report";
            state.exportFormat = null;
          },
        );
    },
  });

export const {
  clearReceiptReport,
  clearReceiptExportError,
} =
  receiptReportSlice.actions;

export default receiptReportSlice.reducer;
